/**
 * Service for Forja Tasks Kanban operations
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type {
  Workspace,
  Column,
  Card,
  ChecklistItem,
  CardWithChecklist,
  ColumnWithCards,
} from '@/types/forjaTasks';

const MAX_COLUMNS_PER_WORKSPACE = 10;
const MAX_CARDS_PER_COLUMN = 10;
type TaskCardUpdate = Database['public']['Tables']['forja_tasks_cards']['Update'];

// Optimized column selections
const WORKSPACE_COLUMNS = 'id, user_id, name, display_order, created_at, updated_at';
const COLUMN_COLUMNS = 'id, workspace_id, user_id, name, display_order, created_at, updated_at';
const CARD_COLUMNS = 'id, column_id, user_id, title, description, cover_image_url, display_order, created_at, updated_at';
const CHECKLIST_COLUMNS = 'id, card_id, user_id, text, is_completed, display_order, created_at';

// ============ Workspaces ============

export async function fetchWorkspaces(): Promise<Workspace[]> {
  const { data, error } = await supabase
    .from('forja_tasks_workspaces')
    .select(WORKSPACE_COLUMNS)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createWorkspace(name: string, userId: string): Promise<Workspace> {
  // Get max display_order
  const { data: existing } = await supabase
    .from('forja_tasks_workspaces')
    .select('display_order')
    .order('display_order', { ascending: false })
    .limit(1);

  const nextOrder = (existing?.[0]?.display_order ?? -1) + 1;

  const { data, error } = await supabase
    .from('forja_tasks_workspaces')
    .insert({
      name,
      user_id: userId,
      display_order: nextOrder,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateWorkspace(id: string, updates: Partial<Pick<Workspace, 'name' | 'display_order'>>): Promise<Workspace> {
  const { data, error } = await supabase
    .from('forja_tasks_workspaces')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteWorkspace(id: string): Promise<void> {
  const { error } = await supabase
    .from('forja_tasks_workspaces')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============ Columns ============

export async function fetchColumns(workspaceId: string): Promise<Column[]> {
  const { data, error } = await supabase
    .from('forja_tasks_columns')
    .select(COLUMN_COLUMNS)
    .eq('workspace_id', workspaceId)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createColumn(workspaceId: string, name: string, userId: string): Promise<Column> {
  // Check column limit
  const { count, error: countError } = await supabase
    .from('forja_tasks_columns')
    .select('*', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId);

  if (countError) throw countError;
  if ((count || 0) >= MAX_COLUMNS_PER_WORKSPACE) {
    throw new Error(`Limite máximo de ${MAX_COLUMNS_PER_WORKSPACE} colunas atingido`);
  }

  // Get max display_order for this workspace
  const { data: existing } = await supabase
    .from('forja_tasks_columns')
    .select('display_order')
    .eq('workspace_id', workspaceId)
    .order('display_order', { ascending: false })
    .limit(1);

  const nextOrder = (existing?.[0]?.display_order ?? -1) + 1;

  const { data, error } = await supabase
    .from('forja_tasks_columns')
    .insert({
      workspace_id: workspaceId,
      user_id: userId,
      name,
      display_order: nextOrder,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateColumn(id: string, updates: Partial<Pick<Column, 'name' | 'display_order'>>): Promise<Column> {
  const { data, error } = await supabase
    .from('forja_tasks_columns')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteColumn(id: string): Promise<void> {
  const { error } = await supabase
    .from('forja_tasks_columns')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function reorderColumns(orders: { id: string; display_order: number }[]): Promise<void> {
  // Update each column's display_order
  const promises = orders.map(({ id, display_order }) =>
    supabase
      .from('forja_tasks_columns')
      .update({ display_order })
      .eq('id', id)
  );

  const results = await Promise.all(promises);
  const firstError = results.find(r => r.error)?.error;
  if (firstError) throw firstError;
}

// ============ Cards ============

export async function fetchCards(workspaceId: string): Promise<CardWithChecklist[]> {
  // First get all columns for this workspace
  const { data: columns, error: colError } = await supabase
    .from('forja_tasks_columns')
    .select('id')
    .eq('workspace_id', workspaceId);

  if (colError) throw colError;

  const columnIds = columns?.map(c => c.id) || [];
  if (columnIds.length === 0) return [];

  // Get all cards for these columns
  const { data: cards, error: cardsError } = await supabase
    .from('forja_tasks_cards')
    .select(CARD_COLUMNS)
    .in('column_id', columnIds)
    .order('display_order', { ascending: true });

  if (cardsError) throw cardsError;

  // Get checklist counts for all cards
  const cardIds = cards?.map(c => c.id) || [];
  if (cardIds.length === 0) return cards || [];

  const { data: checklistItems, error: checkError } = await supabase
    .from('forja_tasks_checklist_items')
    .select('card_id, is_completed')
    .in('card_id', cardIds);

  if (checkError) throw checkError;

  // Compute checklist counts
  const checklistCounts: Record<string, { completed: number; total: number }> = {};
  checklistItems?.forEach(item => {
    if (!checklistCounts[item.card_id]) {
      checklistCounts[item.card_id] = { completed: 0, total: 0 };
    }
    checklistCounts[item.card_id].total++;
    if (item.is_completed) {
      checklistCounts[item.card_id].completed++;
    }
  });

  return (cards || []).map(card => ({
    ...card,
    checklist_completed: checklistCounts[card.id]?.completed || 0,
    checklist_total: checklistCounts[card.id]?.total || 0,
  }));
}

export async function createCard(columnId: string, title: string, userId: string): Promise<Card> {
  // Get max display_order for this column
  const { data: existing } = await supabase
    .from('forja_tasks_cards')
    .select('display_order')
    .eq('column_id', columnId)
    .order('display_order', { ascending: false })
    .limit(1);

  const nextOrder = (existing?.[0]?.display_order ?? -1) + 1;

  const { data, error } = await supabase
    .from('forja_tasks_cards')
    .insert({
      column_id: columnId,
      user_id: userId,
      title,
      display_order: nextOrder,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCard(
  id: string,
  updates: Partial<Pick<Card, 'title' | 'description' | 'cover_image_url' | 'column_id' | 'display_order'>>
): Promise<Card> {
  const { data, error } = await supabase
    .from('forja_tasks_cards')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function deleteCard(id: string): Promise<void> {
  const { error } = await supabase
    .from('forja_tasks_cards')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

export async function moveCard(
  cardId: string,
  newColumnId: string,
  newOrder: number
): Promise<void> {
  const { error } = await supabase
    .from('forja_tasks_cards')
    .update({
      column_id: newColumnId,
      display_order: newOrder,
    })
    .eq('id', cardId);

  if (error) throw error;
}

export async function reorderCards(orders: { id: string; display_order: number; column_id?: string }[]): Promise<void> {
  const promises = orders.map(({ id, display_order, column_id }) => {
    const update: TaskCardUpdate = { display_order };
    if (column_id) update.column_id = column_id;
    
    return supabase
      .from('forja_tasks_cards')
      .update(update)
      .eq('id', id);
  });

  const results = await Promise.all(promises);
  const firstError = results.find(r => r.error)?.error;
  if (firstError) throw firstError;
}

// ============ Checklist Items ============

export async function fetchChecklistItems(cardId: string): Promise<ChecklistItem[]> {
  const { data, error } = await supabase
    .from('forja_tasks_checklist_items')
    .select(CHECKLIST_COLUMNS)
    .eq('card_id', cardId)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data || [];
}

export async function createChecklistItem(cardId: string, text: string, userId: string): Promise<ChecklistItem> {
  // Get max display_order
  const { data: existing } = await supabase
    .from('forja_tasks_checklist_items')
    .select('display_order')
    .eq('card_id', cardId)
    .order('display_order', { ascending: false })
    .limit(1);

  const nextOrder = (existing?.[0]?.display_order ?? -1) + 1;

  const { data, error } = await supabase
    .from('forja_tasks_checklist_items')
    .insert({
      card_id: cardId,
      user_id: userId,
      text,
      display_order: nextOrder,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function toggleChecklistItem(id: string, isCompleted: boolean): Promise<void> {
  const { error } = await supabase
    .from('forja_tasks_checklist_items')
    .update({ is_completed: isCompleted })
    .eq('id', id);

  if (error) throw error;
}

export async function updateChecklistItem(id: string, text: string): Promise<void> {
  const { error } = await supabase
    .from('forja_tasks_checklist_items')
    .update({ text })
    .eq('id', id);

  if (error) throw error;
}

export async function deleteChecklistItem(id: string): Promise<void> {
  const { error } = await supabase
    .from('forja_tasks_checklist_items')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// ============ Image Upload ============

/**
 * Upload a pre-cropped blob as card cover (used after ImageCropModal)
 * The blob is already cropped and compressed by the modal
 */
export async function uploadCroppedCoverImage(blob: Blob, userId: string): Promise<string> {
  const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.webp`;

  const { error: uploadError } = await supabase.storage
    .from('forja-tasks-images')
    .upload(fileName, blob, { 
      upsert: false,
      contentType: 'image/webp'
    });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from('forja-tasks-images')
    .getPublicUrl(fileName);

  return urlData.publicUrl;
}

/**
 * Upload card cover image with automatic cropping (fallback, less preferred)
 * Prefer using ImageCropModal + uploadCroppedCoverImage for user-controlled cropping
 */
export async function uploadCardCoverImage(file: File, userId: string): Promise<string> {
  // Import and crop image to exact dimensions for card consistency
  const { cropToExact } = await import('@/lib/imageUtils');
  
  // Dimensões exatas: 544x256 (2x para retina, aspect ratio 2.125:1)
  // Corresponde ao h-32 (128px) do KanbanCard com largura proporcional
  const blob = await cropToExact(file, {
    width: 544,
    height: 256,
    quality: 0.70,
    format: 'webp'
  });
  
  const fileName = `${userId}/${Date.now()}-${Math.random().toString(36).substring(7)}.webp`;

  const { error: uploadError } = await supabase.storage
    .from('forja-tasks-images')
    .upload(fileName, blob, { 
      upsert: false,
      contentType: 'image/webp'
    });

  if (uploadError) throw uploadError;

  const { data: urlData } = supabase.storage
    .from('forja-tasks-images')
    .getPublicUrl(fileName);

  return urlData.publicUrl;
}

export async function deleteCardCoverImage(url: string): Promise<void> {
  // Extract path from URL
  const bucketUrl = supabase.storage.from('forja-tasks-images').getPublicUrl('').data.publicUrl;
  const path = url.replace(bucketUrl, '');

  if (path) {
    const { error } = await supabase.storage
      .from('forja-tasks-images')
      .remove([path]);

    if (error) throw error;
  }
}

// ============ Helper: Get workspace column count ============

export async function getWorkspaceColumnCount(workspaceId: string): Promise<number> {
  const { count, error } = await supabase
    .from('forja_tasks_columns')
    .select('*', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId);

  if (error) throw error;
  return count || 0;
}

// ============ Duplicate Column ============

export async function duplicateColumn(
  sourceColumnId: string,
  workspaceId: string,
  userId: string
): Promise<{ column: Column; cards: CardWithChecklist[] }> {
  // 1. Fetch source column
  const { data: sourceColumn, error: colError } = await supabase
    .from('forja_tasks_columns')
    .select('*')
    .eq('id', sourceColumnId)
    .single();

  if (colError) throw colError;

  // 2. Check column limit
  const { count } = await supabase
    .from('forja_tasks_columns')
    .select('*', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId);

  if ((count || 0) >= MAX_COLUMNS_PER_WORKSPACE) {
    throw new Error(`Limite máximo de ${MAX_COLUMNS_PER_WORKSPACE} colunas atingido`);
  }

  // 3. Create new column with "(cópia)" suffix
  const newColumn = await createColumn(
    workspaceId,
    `${sourceColumn.name} (cópia)`,
    userId
  );

  // 4. Fetch all cards from source column
  const { data: sourceCards } = await supabase
    .from('forja_tasks_cards')
    .select('*')
    .eq('column_id', sourceColumnId)
    .order('display_order', { ascending: true });

  if (!sourceCards || sourceCards.length === 0) {
    return { column: newColumn, cards: [] };
  }

  // 5. Duplicate each card with its checklist
  const newCards: CardWithChecklist[] = [];
  
  for (const sourceCard of sourceCards) {
    const newCard = await createCard(newColumn.id, sourceCard.title, userId);

    // Update description and cover image if they exist
    if (sourceCard.description || sourceCard.cover_image_url) {
      await updateCard(newCard.id, {
        description: sourceCard.description,
        cover_image_url: sourceCard.cover_image_url,
      });
      newCard.description = sourceCard.description;
      newCard.cover_image_url = sourceCard.cover_image_url;
    }

    // 6. Fetch and duplicate checklist items
    const { data: checklistItems } = await supabase
      .from('forja_tasks_checklist_items')
      .select('*')
      .eq('card_id', sourceCard.id)
      .order('display_order', { ascending: true });

    let checklistTotal = 0;
    if (checklistItems && checklistItems.length > 0) {
      for (const item of checklistItems) {
        await createChecklistItem(newCard.id, item.text, userId);
        checklistTotal++;
      }
    }

    newCards.push({
      ...newCard,
      checklist_completed: 0, // New checklist items start uncompleted
      checklist_total: checklistTotal,
    });
  }

  return { column: newColumn, cards: newCards };
}

export { MAX_COLUMNS_PER_WORKSPACE, MAX_CARDS_PER_COLUMN };
